import React from 'react';
import { Button, Plus } from '@pagespeedify/ui';
import { PanelLeftOpen, Crown, Zap, Bell } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/apiClient';
import { UserAvatarDropdown } from './UserAvatarDropdown';
import { useAuth } from '../lib/auth';
import { Link, useNavigate } from 'react-router-dom';
import { useSidebar } from '../context/SidebarContext';
// removed useTour import since tour button is temporarily disabled

const MobileHeader: React.FC = () => {
  type UnreadCountResponse = {
    data?: {
      unreadCount?: number;
    };
  };

  const { isSidebarOpen, setIsSidebarOpen } = useSidebar();
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const apiClient = useApi();
  const { data: unreadCountData } = useQuery<UnreadCountResponse>({
    queryKey: ['unreadNotificationsCount'],
    queryFn: () => apiClient.get('/notifications/unread-count'),
    enabled: !!user,
    retry: (failureCount, error: any) => {
      const isNetworkError =
        !error?.response ||
        error?.code === 'ERR_NETWORK' ||
        error?.message === 'Network Error';
      if (!isNetworkError) return false;
      return failureCount < 1;
    },
    refetchInterval: (query) => {
      const error: any = query.state.error;
      const isNetworkError =
        !!error &&
        (!error?.response ||
          error?.code === 'ERR_NETWORK' ||
          error?.message === 'Network Error');
      return isNetworkError ? 300000 : 60000;
    },
  });

  const unreadCount = unreadCountData?.data?.unreadCount || 0;

  const handleMenuClick = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // tour handler removed while data-tour is disabled

  return (
    <header className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b border-border shadow-sm">
      <div className="flex items-center justify-between px-3 py-2.5 sm:px-4 sm:py-3">
        {/* Left Section - Menu Button (only when sidebar is closed) */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          {!isSidebarOpen && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleMenuClick}
              className="h-8 w-8 hover:bg-muted transition-colors flex-shrink-0"
            >
              <PanelLeftOpen className="h-5 w-5" />
            </Button>
          )}
        </div>

        {/* Right Section - User Info & Actions */}
        <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
          {user && (
            <>
              {/* Quick Actions */}
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 hover:bg-muted transition-colors"
                onClick={() => navigate('/agent')}
              >
                <Plus className="h-5 w-5" />
              </Button>

              {/* Notifications */}
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 relative text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                onClick={() => navigate('/notifications')}
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-background" />
                )}
              </Button>

              {/* User Status Cards */}
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Subscription Status */}
                <Link
                  to="/subscriptions"
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all hover:scale-105 active:scale-95 ${isAdmin
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-sm'
                    : user?.user_type === 'unsubscribed'
                      ? 'bg-gradient-to-r from-gray-500 to-slate-500 text-white shadow-sm'
                      : user?.user_type === 'enterprise'
                        ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-sm'
                        : user?.user_type === 'pro'
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-sm'
                          : user?.user_type === 'basic'
                            ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-sm'
                            : 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-sm'
                    }`}
                >
                  <Crown className="h-3 w-3" />
                  <span className="capitalize">
                    {user?.user_type === 'enterprise' ? 'Ent.' :
                      user?.user_type === 'pro' ? 'Pro' :
                        user?.user_type === 'basic' ? 'Basic' :
                          user?.user_type === 'unsubscribed' ? 'Free' :
                            user?.user_type === 'admin' ? 'Admin' :
                              user?.user_type}
                  </span>
                </Link>

                {/* Credits */}
                {typeof user?.credits === 'number' && (
                  <Link
                    to="/credits"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-medium shadow-sm hover:scale-105 active:scale-95 transition-all"
                  >
                    <Zap className="h-3 w-3" />
                    <span className="mr-1">Credits</span>
                    <span className="font-bold">{user.credits}</span>
                  </Link>
                )}
              </div>

              {/* User Avatar */}
              <UserAvatarDropdown user={user} onLogout={logout} />
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default MobileHeader;
